import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Briefcase, Shield } from "lucide-react";
import BrandedHeader from "@/components/BrandedHeader";

const INPUT_TYPE_BY_FIELD = { number: "number", email: "email" };
const getInputType = (fieldType) => INPUT_TYPE_BY_FIELD[fieldType] || "text";

/**
 * Pre-screening form rendered before the actual assessment. Shared by
 * CandidateTakeTestPage and PublicTestPage.
 */
export default function PreScreeningSection({
  questions,
  answers,
  setAnswers,
  onSubmit,
  headerTitle,
  headerSubtitle,
}) {
  const setAnswer = (key, value) => setAnswers({ ...answers, [key]: value });

  const renderField = (q, idx) => {
    const common = {
      value: answers[q.question] || "",
      onChange: (e) => setAnswer(q.question, e.target.value),
      "data-testid": `pre-screening-${idx}`,
    };
    if (q.field_type === "textarea") {
      return (
        <textarea
          {...common}
          placeholder={q.placeholder || "Enter your answer..."}
          rows={3}
          className="flex w-full rounded-lg border border-input bg-card px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 resize-none"
        />
      );
    }
    if (q.field_type === "select" && q.options) {
      return (
        <select
          {...common}
          className="flex w-full rounded-lg border border-input bg-card px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
        >
          <option value="">-- Select --</option>
          {q.options.map((opt) => (
            <option key={`${q.question}-${opt}`} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }
    return (
      <Input
        {...common}
        type={getInputType(q.field_type)}
        placeholder={q.placeholder || "Enter your answer..."}
        className="bg-card"
      />
    );
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="pre-screening-section">
      <BrandedHeader className="flex-shrink-0">
        <div className="flex items-center gap-3">
          <Briefcase className="w-5 h-5 text-primary" />
          <div>
            <span className="font-display font-semibold text-sm">{headerTitle}</span>
            {headerSubtitle && (
              <span className="text-xs text-muted-foreground ml-2">{headerSubtitle}</span>
            )}
          </div>
        </div>
        <Badge variant="secondary" className="text-xs ml-auto">Pre-Screening</Badge>
      </BrandedHeader>

      <div className="flex-1 px-4 sm:px-6 py-6 overflow-auto">
        <div className="max-w-3xl mx-auto">
          <Card className="border-border bg-card mb-6">
            <CardContent className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <h2 className="text-lg font-display font-semibold mb-2">Pre-Screening Information</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Before starting the assessment, please answer the following questions. All fields are required.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-card" data-testid="pre-screening-form">
            <CardContent className="p-6 space-y-5">
              {questions.map((q, idx) => (
                <div key={q.question || `pre-q-${idx}`}>
                  <label className="text-sm font-medium mb-2 block">
                    {q.question}
                    {q.required && <span className="text-[hsl(var(--destructive))] ml-1">*</span>}
                  </label>

                  {renderField(q, idx)}
                </div>
              ))}

              <div className="pt-4">
                <Button onClick={onSubmit} className="w-full btn-press" data-testid="pre-screening-submit-button">
                  Continue to Assessment
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

/**
 * Shared pre-screening validator. Returns `null` on success or an error string.
 * Exported so pages can call before transitioning away.
 */
export function validatePreScreening(questions, answers) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  for (const q of questions) {
    const answer = answers[q.question]?.trim();
    if (q.required && !answer) {
      return `Please answer: ${q.question}`;
    }
    if (q.field_type === "email" && answer && !emailRegex.test(answer)) {
      return `Please enter a valid email address for: ${q.question}`;
    }
  }
  return null;
}
